// src/pages/PublicProperty.jsx - V24 MINIMALIST ENTERPRISE
// ======================================================
// 🔥 V24 ULTRA MINIMALIST — Airbnb / QuintoAndar App Nativa Grade
// ======================================================
// 🔥 V24: Sección "Imóveis que você pode gostar" con fondo BLANCO puro
// 🔥 V24: Scroll horizontal 100% visible tipo Airbnb (sin fondo gris)
// 🔥 V23: Diseño ultra limpio — Sin bloque oscuro, iconos pequeños, tipografía nativa
// 🔥 V23: Sección "Imóveis que você pode gostar" com scroll horizontal tipo Airbnb
// 🔥 V23: Rating dinámico — solo muestra si hay datos reales
// 🔥 V23: Características con iconos w-5 h-5 (20px móvil) / w-6 h-6 (24px desktop)
// 🔥 V23: Sección precio en tarjeta blanca con borde sutil
// 🔥 V23: Detalles del inmueble con divisores finos (Airbnb grade)
// 🔥 V23: Sticky CTA bar más compacta en móvil
// 🔥 V21: CASCADE RESOLVER — 3 capas de búsqueda (Airbnb/Zillow grade)
// 🔥 V21: CAPA 1: public_properties (cache global)
// 🔥 V21: CAPA 2: companies/{cid}/properties (datos privados) → auto-publica en capa 1
// 🔥 V21: CAPA 3: URL params → datos básicos + WhatsApp funcional
// 🔥 V21: LINK PÚBLICO GARANTIDO — Nunca más un link roto
// 🔥 V20: StrategicShareModal — compartir con mensajes por audiencia
// ======================================================
import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { getPublicDb } from "../lib/firebase/firestorePublic";
import { getApp } from "firebase/app";
import { doc, getDoc, onSnapshot, collection, query, where, getDocs, limit, updateDoc, increment, setDoc, getFirestore } from "firebase/firestore";
import { useParams, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  MapPin, ChevronLeft, ChevronRight, MessageCircle, Home, Maximize2, X,
  Share2, Copy, Check, Bed, Bath, Ruler, Building2, Calendar, Phone, Mail,
  Award, TrendingUp, DollarSign, Heart, Shield, Wifi, Car, Coffee, Info,
  Camera, Sparkles, User, Clock, CheckCircle, ShieldCheck, Zap,
  Eye, Users, Navigation, ChevronDown, ChevronUp, ArrowLeft, RefreshCw,
  Star
} from "lucide-react";
import { getPublicPropertyUrl } from "../config/urls";
// ======================================================
// 🔥 V23: COMPONENTES
// ======================================================
import RecommendedProperties from "../components/recommendations/RecommendedProperties";
import { IlhaLeadsSmartTags, getBestTag } from "../components/recommendations/IlhaLeadsSmartTags.jsx";
import { IlhaLeadsPriceIntelligence, calculatePriceContext } from "../components/recommendations/IlhaLeadsPriceIntelligence.jsx";
import { IlhaLeadsSocialProof } from "../components/recommendations/IlhaLeadsSocialProof.jsx";
import { IlhaLeadsUrgencyEngine, calculateUrgencyContext } from "../components/recommendations/IlhaLeadsUrgencyEngine.jsx";
import StrategicShareModal from "../components/Properties/StrategicShareModal";
import { FloatingShareButton, PillShareButton } from "../components/Properties/ShareButton";
import HorizontalScroll from "../components/HorizontalScroll";
import FeaturedPropertyCard from "../components/FeaturedPropertyCard";
// ======================================================
// 🔧 FUNÇÕES AUXILIARES (PURAS)
// ======================================================
const safeNum = (value, fallback = 0) => {
  if (value === null || value === undefined) return fallback;
  const num = Number(value);
  return isNaN(num) || !isFinite(num) ? fallback : num;
};
const formatPriceFull = (price) => {
  const num = safeNum(price);
  if (!num) return 'Valor sob consulta';
  try {
    return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 2 });
  } catch { return 'Valor sob consulta'; }
};
const extractClientName = (searchParams) => {
  const nome = searchParams.get('nome') || searchParams.get('name') ||
    searchParams.get('cliente') || searchParams.get('customer');
  if (!nome) return "Cliente";
  return nome.split(' ').map(p => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()).slice(0, 2).join(' ');
};
const getPrivateDb = () => {
  try {
    const app = getApp();
    return getFirestore(app);
  } catch (e) {
    console.warn('⚠️ [V24] No se pudo obtener app principal, usando pública:', e.message);
    return getPublicDb();
  }
};
const getWhatsAppLink = (company, url, nomeCliente = "Cliente", property) => {
  const phone = company?.telefoneWhatsApp ||
    company?.telefoneCelular ||
    company?.telefone ||
    "";
  let cleanPhone = phone.replace(/\D/g, "");
  if ((cleanPhone.length === 10 || cleanPhone.length === 11) && !cleanPhone.startsWith("55")) {
    cleanPhone = "55" + cleanPhone;
  }
  const finalPhone = cleanPhone.length >= 12 ? cleanPhone : "";
  const title = property?.title || "Imóvel";
  const price = property?.price ? `R$ ${Number(property.price).toLocaleString("pt-BR")}` : "Consultar";
  const quartos = property?.bedrooms ? `\u{1F6CF}\u{FE0F} *Quartos:* ${property.bedrooms}%0A` : "";
  const area = property?.area ? `\u{1F4D0} *Área:* ${property.area}m²%0A` : "";
  const message = `*Olá, ${nomeCliente}!* \u{1F44B}%0A%0A` +
    `Vi este imóvel e fiquei interessado! \u{1F3E0}%0A%0A` +
    `\u{1F4CD} *Imóvel:* ${encodeURIComponent(title)}%0A` +
    `\u{1F4B0} *Valor:* ${encodeURIComponent(price)}%0A` +
    quartos + area +
    `%0A\u{1F4CC} *Link:* ${encodeURIComponent(url)}%0A%0A` +
    `Poderia me passar mais informações? O imóvel ainda está disponível?`;
  return finalPhone
    ? `https://wa.me/${finalPhone}?text=${message}`
    : `https://wa.me/?text=${message}`;
};
// ======================================================
// 🔥 V21: CASCADE RESOLVER — 3 CAPAS DE BÚSQUEDA
// ======================================================
const fetchFromPublicProperties = async (propertyId) => {
  const db = getPublicDb();
  try {
    const propertyRef = doc(db, "public_properties", propertyId);
    const propertySnap = await getDoc(propertyRef);
    if (propertySnap.exists()) {
      const data = propertySnap.data();
      if (data.isPublic !== false) {
        try { await updateDoc(propertyRef, { views: increment(1) }); } catch (e) {}
        return { found: true, source: 'public_properties', property: { id: propertySnap.id, ...data } };
      }
    }
    return { found: false, source: 'public_properties' };
  } catch (error) {
    console.warn('⚠️ [V24] Error en CAPA 1:', error.message);
    return { found: false, source: 'public_properties', error: error.message };
  }
};
const fetchFromCompanyProperties = async (propertyId, companyId) => {
  if (!companyId) return { found: false, source: 'company_properties', reason: 'no_company_id' };
  try {
    const db = getPrivateDb();
    const propertyRef = doc(db, "companies", companyId, "properties", propertyId);
    const propertySnap = await getDoc(propertyRef);
    if (propertySnap.exists()) {
      const data = propertySnap.data();
      console.log('✅ [V24] CAPA 2: Encontrado en companies/', companyId, '/properties/', propertyId);
      try {
        const publicDb = getPublicDb();
        const publicRef = doc(publicDb, "public_properties", propertyId);
        const now = new Date().toISOString();
        await setDoc(publicRef, {
          id: propertyId, title: data.title || 'Imóvel sem título', description: data.description || '',
          price: data.price || null, type: data.type || null, status: data.status || 'Disponível',
          address: data.address || '', city: data.city || '', neighborhood: data.neighborhood || '',
          bedrooms: data.bedrooms || 0, bathrooms: data.bathrooms || 0, area: data.area || 0,
          images: data.images || [], previewImage: data.previewImage || null, featured: data.featured || false,
          companyId: companyId, userId: data.userId || data.createdBy || null,
          ownerId: data.ownerId || data.userId || null, ownerName: data.ownerName || null,
          ownerEmail: data.ownerEmail || null, isPublic: true,
          publishedAt: data.publishedAt || now, updatedAt: now, views: 1, saves: 0,
          company: data.company || null,
        }, { merge: true });
        console.log('🔄 [V24] Auto-publicado en public_properties:', propertyId);
      } catch (pubError) { console.warn('⚠️ [V24] No se pudo auto-publicar:', pubError.message); }
      return { found: true, source: 'company_properties', property: { id: propertySnap.id, ...data } };
    }
    return { found: false, source: 'company_properties' };
  } catch (error) {
    console.warn('⚠️ [V24] Error en CAPA 2:', error.message);
    return { found: false, source: 'company_properties', error: error.message };
  }
};
const buildFromParams = (propertyId, searchParams) => {
  const title = searchParams.get('title') || searchParams.get('titulo');
  const price = searchParams.get('price') || searchParams.get('preco');
  const city = searchParams.get('city') || searchParams.get('cidade');
  const type = searchParams.get('type') || searchParams.get('tipo');
  const cid = searchParams.get('cid') || searchParams.get('companyId');
  const uid = searchParams.get('uid') || searchParams.get('userId');
  if (!title && !cid) return { found: false, source: 'params', reason: 'insufficient_data' };
  console.log('✅ [V24] CAPA 3: Construido desde URL params');
  return {
    found: true, source: 'params',
    property: {
      id: propertyId, title: title || 'Imóvel', price: price ? safeNum(price) : null,
      city: city || '', type: type || 'Imóvel', status: 'Disponível',
      companyId: cid || null, userId: uid || null, description: '', images: [],
      bedrooms: 0, bathrooms: 0, area: 0, views: 0, saves: 0, isPublic: true, _fromParams: true,
    }
  };
};
const validatePublicAccess = async (propertyId, searchParams) => {
  const capa1 = await fetchFromPublicProperties(propertyId);
  if (capa1.found) return { valid: true, property: capa1.property, source: capa1.source };
  const cid = searchParams.get('cid') || searchParams.get('companyId') || null;
  if (cid) {
    const capa2 = await fetchFromCompanyProperties(propertyId, cid);
    if (capa2.found) return { valid: true, property: capa2.property, source: capa2.source };
  }
  const capa3 = buildFromParams(propertyId, searchParams);
  if (capa3.found) return { valid: true, property: capa3.property, source: capa3.source, fallback: true };
  return {
    valid: false,
    error: 'Imóvel não encontrado. O link pode estar incorreto ou o imóvel foi removido.',
    tried: ['public_properties', cid ? 'company_properties' : null, 'params'].filter(Boolean)
  };
};
// ======================================================
// 🎉 BIENVENIDA ESTRATÉGICA
// ======================================================
const StrategicWelcome = ({ nomeCliente, empresa, onClose, property }) => {
  const [show, setShow] = useState(true);
  const handleClose = useCallback(() => { setShow(false); setTimeout(onClose, 300); }, [onClose]);
  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, y: -50, scale: 0.9 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -50, scale: 0.9 }}
          className="fixed top-20 left-1/2 transform -translate-x-1/2 z-[99999] w-full max-w-md px-4"
        >
          <div className="relative overflow-hidden rounded-2xl shadow-2xl"
            style={{ background: `linear-gradient(135deg, ${empresa?.cor || '#2563eb'} 0%, ${empresa?.cor || '#2563eb'}dd 100%)` }}>
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer" />
            <div className="relative z-10 p-6 text-white">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0"><div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm"><User className="w-6 h-6 text-white" /></div></div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold mb-1 flex items-center gap-2"><Sparkles className="w-5 h-5" /> ⚡ OPORTUNIDADE ÚNICA!</h3>
                  <p className="text-white/90 text-sm mb-3">Este imóvel está disponível e o corretor está online agora mesmo!</p>
                  <div className="space-y-1 mb-3">
                    <div className="flex items-center gap-2 text-xs text-white/80"><CheckCircle className="w-3 h-3" /><span>Resposta em menos de 5 minutos</span></div>
                    <div className="flex items-center gap-2 text-xs text-white/80"><Clock className="w-3 h-3" /><span>Agende uma visita hoje</span></div>
                  </div>
                  <motion.a whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                    href={getWhatsAppLink(empresa, getPublicPropertyUrl(property?.id), nomeCliente, property)}
                    target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-white text-gray-900 rounded-lg text-sm font-semibold shadow-lg hover:shadow-xl transition-all">
                    <MessageCircle className="w-4 h-4 text-green-600" /> Falar com Corretor
                  </motion.a>
                </div>
                <button onClick={handleClose} className="flex-shrink-0 w-8 h-8 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition"><X className="w-4 h-4 text-white" /></button>
              </div>
            </div>
            <motion.div initial={{ width: "100%" }} animate={{ width: "0%" }} transition={{ duration: 8, ease: "linear" }} className="absolute bottom-0 left-0 h-1 bg-white/30" />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
// ======================================================
// 🖼️ GALERÍA PREMIUM — CON SWIPE TÁCTIL + DOTS
// 🔥 V26: ULTRA MODERN GALLERY — Modo claro estilo Airbnb/Booking
// ======================================================
const ImageGallery = ({ images, activeImage, onPrev, onNext, onSelect, primaryColor, isMobile }) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const handleTouchStart = useCallback((e) => { touchStartX.current = e.touches[0].clientX; }, []);
  const handleTouchMove = useCallback((e) => { touchEndX.current = e.touches[0].clientX; }, []);
  const handleTouchEnd = useCallback(() => {
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 50) { if (diff > 0) onNext(); else onPrev(); }
  }, [onNext, onPrev]);
  if (!images || images.length === 0) {
    return (
      <div className="w-full aspect-[16/9] rounded-xl bg-gradient-to-br from-slate-100 to-slate-200 flex flex-col items-center justify-center text-slate-500 p-8">
        <Camera className="w-16 h-16 mb-4 text-slate-400" />
        <p className="text-lg font-medium text-center">Imagens não disponíveis publicamente</p>
        <p className="text-sm text-center mt-2 max-w-md text-slate-500">Para visualizar todas as fotos, entre em contato com o corretor.</p>
      </div>
    );
  }
  return (
    <>
      <div className="relative w-full aspect-[16/9] rounded-xl overflow-hidden bg-gray-100 group"
        onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}>
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
            <div className="w-12 h-12 border-4 border-slate-200 border-t-slate-500 rounded-full animate-spin" />
          </div>
        )}
        <motion.img key={activeImage} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}
          src={images[activeImage]} alt={`Imagem ${activeImage + 1}`}
          className="w-full h-full object-cover cursor-pointer"
          onLoad={() => setIsLoading(false)} onClick={() => setIsFullscreen(true)} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/10 via-transparent to-transparent pointer-events-none" />
        <div className="absolute top-4 right-4 px-3 py-1.5 bg-white/90 backdrop-blur-sm rounded-full text-slate-800 text-sm shadow-md z-10">
          <span className="font-bold">{activeImage + 1}</span> / {images.length}
        </div>
        {images.length > 1 && (
          <>
            <button onClick={onPrev} className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-white/90 hover:bg-white text-slate-700 rounded-full shadow-md hover:shadow-lg z-10 transition-all"><ChevronLeft className="w-5 h-5" /></button>
            <button onClick={onNext} className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-white/90 hover:bg-white text-slate-700 rounded-full shadow-md hover:shadow-lg z-10 transition-all"><ChevronRight className="w-5 h-5" /></button>
          </>
        )}
        <button onClick={() => setIsFullscreen(true)} className="absolute top-4 left-4 p-2 bg-white/90 hover:bg-white text-slate-700 rounded-lg shadow-md hover:shadow-lg z-10 transition-all"><Maximize2 className="w-5 h-5" /></button>
        {images.length > 1 && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
            {images.map((_, idx) => (
              <button key={idx} onClick={() => onSelect(idx)}
                className={`w-2 h-2 rounded-full transition-all shadow-sm ${idx === activeImage ? 'bg-white scale-110' : 'bg-white/60 hover:bg-white/90'}`} />
            ))}
          </div>
        )}
      </div>
      {images.length > 1 && (
        <div className="flex gap-2 overflow-x-auto py-2 mt-2 scrollbar-hide">
          {images.map((img, idx) => (
            <button
              key={idx}
              onClick={() => onSelect(idx)}
              className={`flex-shrink-0 ${isMobile ? 'w-12 h-10' : 'w-16 h-12'} rounded-lg overflow-hidden border-2 bg-slate-100 transition-all duration-200 ${
                idx === activeImage
                  ? 'shadow-lg scale-105 opacity-100'
                  : 'border-transparent opacity-60 hover:opacity-90 hover:border-slate-300'
              }`}
              style={idx === activeImage ? { borderColor: primaryColor } : {}}
            >
              <img
                src={img}
                alt={`Miniatura ${idx + 1}`}
                className="w-full h-full object-cover"
                loading="lazy"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentElement.style.background = '#f1f5f9';
                }}
              />
            </button>
          ))}
        </div>
      )}
      <AnimatePresence>
        {isFullscreen && <FullscreenGallery images={images} initialIndex={activeImage} onClose={() => setIsFullscreen(false)} />}
      </AnimatePresence>
    </>
  );
};
// ======================================================
// 🖼️ FULLSCREEN GALLERY
// ======================================================
const FullscreenGallery = ({ images, initialIndex, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const handlePrev = useCallback(() => setCurrentIndex(prev => prev === 0 ? images.length - 1 : prev - 1), [images.length]);
  const handleNext = useCallback(() => setCurrentIndex(prev => prev === images.length - 1 ? 0 : prev + 1), [images.length]);
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') onClose();
    if (e.key === 'ArrowLeft') handlePrev();
    if (e.key === 'ArrowRight') handleNext();
  }, [onClose, handlePrev, handleNext]);
  useEffect(() => { window.addEventListener('keydown', handleKeyDown); return () => window.removeEventListener('keydown', handleKeyDown); }, [handleKeyDown]);
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100000] bg-white flex items-center justify-center" onClick={onClose}>
      <button onClick={onClose} className="absolute top-6 right-6 p-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-full shadow-md z-10"><X className="w-6 h-6" /></button>
      <div className="absolute top-6 left-6 px-4 py-2 bg-slate-100 text-slate-700 rounded-full shadow-md z-10">{currentIndex + 1} / {images.length}</div>
      <motion.div key={currentIndex} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
        className="w-full h-full flex items-center justify-center p-4 md:p-12" onClick={(e) => e.stopPropagation()}>
        <img src={images[currentIndex]} alt="" className="max-w-full max-h-full object-contain rounded-lg shadow-xl" />
      </motion.div>
      {images.length > 1 && (
        <>
          <button onClick={(e) => { e.stopPropagation(); handlePrev(); }} className="absolute left-4 top-1/2 -translate-y-1/2 p-4 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-full shadow-md"><ChevronLeft className="w-8 h-8" /></button>
          <button onClick={(e) => { e.stopPropagation(); handleNext(); }} className="absolute right-4 top-1/2 -translate-y-1/2 p-4 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-full shadow-md"><ChevronRight className="w-8 h-8" /></button>
        </>
      )}
    </motion.div>
  );
};
// ======================================================
// 🏆 COMPONENTE PRINCIPAL — V24 MINIMALIST ENTERPRISE
// ======================================================
export default function PublicProperty() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [property, setProperty] = useState(null);
  const [company, setCompany] = useState(null);
  const [activeImage, setActiveImage] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showWelcome, setShowWelcome] = useState(false);
  const [nomeCliente, setNomeCliente] = useState("Cliente");
  const [error, setError] = useState(null);
  const [allProperties, setAllProperties] = useState([]);
  const [showShareModal, setShowShareModal] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [dataSource, setDataSource] = useState(null);
  const mountedRef = useRef(true);
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 100);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  useEffect(() => { setNomeCliente(extractClientName(searchParams)); }, [searchParams]);
  useEffect(() => {
    if (!property?.companyId) return;
    let unsubscribe = () => { };
    let mounted = true;
    try {
      const db = getPublicDb();
      const compRef = doc(db, "public_companies", property.companyId);
      unsubscribe = onSnapshot(compRef, (compSnap) => {
        if (!mounted) return;
        if (compSnap.exists()) setCompany(compSnap.data());
        else setCompany(prev => prev?.nome ? prev : { nome: property?.ownerName || "Imobiliária", cor: "#2563eb", logo: null });
      }, (err) => { console.warn('⚠️ [V24] Error listener compañía:', err.message); });
    } catch (e) { console.warn('⚠️ [V24] Error setup listener:', e.message); }
    return () => { mounted = false; unsubscribe(); };
  }, [property?.companyId]);
  useEffect(() => {
    mountedRef.current = true;
    const loadProperty = async () => {
      try {
        setLoading(true); setError(null);
        const validation = await validatePublicAccess(id, searchParams);
        if (!validation.valid) {
          if (mountedRef.current) { setError(validation.error); setLoading(false); }
          return;
        }
        const publicProperty = validation.property;
        if (validation.source) setDataSource(validation.source);
        let imagesToShow = [];
        if (publicProperty.images?.length > 0) {
          imagesToShow = publicProperty.images.filter(img => typeof img === 'string' && (img.startsWith('data:') || img.startsWith('http')));
        }
        if (mountedRef.current) {
          setProperty({
            ...publicProperty, images: imagesToShow,
            description: publicProperty.description || '',
            bedrooms: safeNum(publicProperty.bedrooms), bathrooms: safeNum(publicProperty.bathrooms),
            area: safeNum(publicProperty.area), type: publicProperty.type || 'Imóvel',
            neighborhood: publicProperty.neighborhood || '',
            iptu: publicProperty.iptu || null, condoFee: publicProperty.condoFee || null,
            views: safeNum(publicProperty.views), saves: safeNum(publicProperty.saves),
            rating: publicProperty.rating || null
          });
        }
        let companyData = publicProperty.company;
        if (!companyData?.nome) {
          try { const db = getPublicDb(); const compSnap = await getDoc(doc(db, "public_companies", publicProperty.companyId)); if (compSnap.exists()) companyData = compSnap.data(); } catch { }
          if (!companyData?.nome) companyData = { nome: publicProperty.ownerName || "Ilha Leads Imóveis", cor: "#2563eb", logo: null };
        }
        if (mountedRef.current) setCompany(companyData);
        try {
          const db = getPublicDb(); const cid = publicProperty.companyId || publicProperty.company;
          if (cid) { const q = query(collection(db, "public_properties"), where("companyId", "==", cid), limit(50)); const snap = await getDocs(q); if (mountedRef.current) { setAllProperties(snap.docs.map(d => ({ id: d.id, ...d.data() }))); } }
        } catch (e) { console.warn('⚠️ [V24] No se cargaron propiedades para recomendaciones:', e.message); }
        setTimeout(() => { if (mountedRef.current) setShowWelcome(true); }, 1000);
        console.log(`✅ [V24] Propiedad cargada desde: ${validation.source}${validation.fallback ? ' (fallback)' : ''}`);
      } catch (err) { console.error("❌ [V24] Error crítico:", err); if (mountedRef.current) setError('Erro ao carregar imóvel.'); }
      finally { if (mountedRef.current) setLoading(false); }
    };
    if (id) loadProperty();
    return () => { mountedRef.current = false; };
  }, [id, searchParams]);
  const prevImage = useCallback(() => { if (property?.images?.length) setActiveImage(prev => prev === 0 ? property.images.length - 1 : prev - 1); }, [property?.images?.length]);
  const nextImage = useCallback(() => { if (property?.images?.length) setActiveImage(prev => prev === property.images.length - 1 ? 0 : prev + 1); }, [property?.images?.length]);
  const primaryColor = company?.cor || '#2563eb';
  const companyId = property?.companyId || property?.company || null;
  const propertyUrl = property ? getPublicPropertyUrl(property.id) : '';
  const detailContext = useMemo(() => {
    if (!allProperties.length || !property) return {};
    const urgencyCtx = calculateUrgencyContext(allProperties, property);
    const priceCtx = calculatePriceContext(allProperties);
    return { ...urgencyCtx, ...priceCtx, allProperties };
  }, [allProperties, property]);
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="text-center">
          <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Carregando imóvel...</p>
        </motion.div>
      </div>
    );
  }
  if (error || !property) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center max-w-md px-4">
          <p className="text-6xl mb-4">🏠</p>
          <p className="text-xl text-slate-600 font-medium">{error || 'Imóvel não encontrado'}</p>
          <p className="text-sm text-slate-400 mt-2">O link pode estar incorreto ou o imóvel foi removido.</p>
        </motion.div>
      </div>
    );
  }
  const priceFormatted = formatPriceFull(property.price);
  const iptuFormatted = property.iptu ? `R$ ${safeNum(property.iptu).toLocaleString('pt-BR')}` : null;
  const condoFeeFormatted = property.condoFee ? `R$ ${safeNum(property.condoFee).toLocaleString('pt-BR')}` : null;
  const rating = property?.rating || (property?.views > 100 ? 4.5 : null);
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <AnimatePresence>
        {showWelcome && (
          <StrategicWelcome nomeCliente={nomeCliente} empresa={company} onClose={() => setShowWelcome(false)} property={property} />
        )}
      </AnimatePresence>
      <motion.div
        initial={{ y: -100 }} animate={{ y: 0 }}
        className={`sticky top-0 z-50 transition-all duration-300 ${scrolled ? 'backdrop-blur-xl shadow-lg' : 'backdrop-blur-md shadow-md'}`}
        style={{ background: scrolled ? `${primaryColor}ee` : `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%)` }}
      >
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            {company?.logo ? (
              <img src={company.logo} alt={company.nome} className="h-10 w-auto rounded-lg shadow flex-shrink-0" />
            ) : (
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <Building2 className="w-6 h-6 text-white" />
              </div>
            )}
            <div className="min-w-0">
              <h1 className="text-white font-bold text-base md:text-lg truncate flex items-center gap-2">
                {company?.nome || 'Imobiliária'}
                <ShieldCheck className="w-4 h-4 text-emerald-300 flex-shrink-0" />
              </h1>
              <p className="text-white/80 text-xs truncate">{company?.slogan || 'Seu imóvel dos sonhos'}</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <PillShareButton property={property} primaryColor="#ffffff" label="" size="sm" className="!bg-white/20 !text-white hover:!bg-white/30 !shadow-none" />
          </div>
        </div>
      </motion.div>
      <div className="max-w-7xl mx-auto px-4 py-6 md:py-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <ImageGallery images={property.images} activeImage={activeImage} onPrev={prevImage} onNext={nextImage}
            onSelect={setActiveImage} primaryColor={primaryColor} isMobile={isMobile} />
          <div className="p-4 md:p-6 space-y-5">
            {dataSource && dataSource !== 'public_properties' && (
              <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
                <RefreshCw className="w-3 h-3" />
                <span>Imóvel recuperado via link direto</span>
              </div>
            )}
            <div className="space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-xl md:text-2xl font-bold text-slate-900">{property.title}</h1>
                <IlhaLeadsSmartTags property={property} context={detailContext} maxTags={2} compact />
              </div>
              <div className="flex items-center gap-1.5 text-slate-500 text-sm">
                <MapPin className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">{property.address || 'Endereço não informado'}</span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                {rating && (
                  <>
                    <span className="flex items-center gap-1">
                      <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                      <span className="font-semibold text-slate-800">{rating}</span>
                    </span>
                    <span className="text-slate-300">·</span>
                  </>
                )}
                <span className="text-slate-500">{property.views || 0} visualizações</span>
                <span className="text-slate-300">·</span>
                <span className="text-slate-500">{property.saves || 0} salvos</span>
              </div>
              <div className="flex items-center gap-2 flex-wrap pt-1">
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  property.status === 'Disponível' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                  property.status === 'Vendido' ? 'bg-rose-50 text-rose-700 border border-rose-200' : 'bg-amber-50 text-amber-700 border border-amber-200'}`}>
                  {property.status || 'Disponível'}
                </span>
                {property.featured && (
                  <span className="px-3 py-1 bg-yellow-50 text-yellow-700 border border-yellow-200 rounded-full text-xs font-semibold flex items-center gap-1">
                    <Award className="w-3.5 h-3.5" /> Destaque
                  </span>
                )}
              </div>
            </div>
            <div className="bg-gradient-to-r from-blue-50 to-white border border-blue-100 rounded-2xl p-5">
              <p className="text-xs font-medium text-blue-500 uppercase tracking-wider mb-1">Valor do Imóvel</p>
              <p className="text-2xl md:text-3xl font-bold text-blue-700">{priceFormatted}</p>
              <IlhaLeadsUrgencyEngine property={property} context={detailContext} variant="compact" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              {safeNum(property.bedrooms) > 0 && (
                <div className="flex items-center gap-3 bg-slate-50 rounded-xl p-4">
                  <Bed className="w-5 h-5 md:w-6 md:h-6 text-slate-500 flex-shrink-0" />
                  <div>
                    <p className="text-lg font-bold text-slate-900">{property.bedrooms}</p>
                    <p className="text-[11px] text-slate-500">Quarto{property.bedrooms > 1 ? 's' : ''}</p>
                  </div>
                </div>
              )}
              {safeNum(property.bathrooms) > 0 && (
                <div className="flex items-center gap-3 bg-slate-50 rounded-xl p-4">
                  <Bath className="w-5 h-5 md:w-6 md:h-6 text-slate-500 flex-shrink-0" />
                  <div>
                    <p className="text-lg font-bold text-slate-900">{property.bathrooms}</p>
                    <p className="text-[11px] text-slate-500">Banheiro{property.bathrooms > 1 ? 's' : ''}</p>
                  </div>
                </div>
              )}
              {safeNum(property.area) > 0 && (
                <div className="flex items-center gap-3 bg-slate-50 rounded-xl p-4">
                  <Ruler className="w-5 h-5 md:w-6 md:h-6 text-slate-500 flex-shrink-0" />
                  <div>
                    <p className="text-lg font-bold text-slate-900">{property.area}</p>
                    <p className="text-[11px] text-slate-500">m²</p>
                  </div>
                </div>
              )}
              {property.type && (
                <div className="flex items-center gap-3 bg-slate-50 rounded-xl p-4">
                  <Home className="w-5 h-5 md:w-6 md:h-6 text-slate-500 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-bold text-slate-900 truncate">{property.type}</p>
                    <p className="text-[11px] text-slate-500">Tipo</p>
                  </div>
                </div>
              )}
            </div>
            <IlhaLeadsSocialProof property={property} company={company} variant="full" />
            <div className="space-y-5 pt-2">
              {property.description && (
                <div>
                  <h3 className="text-base font-semibold text-slate-900 mb-2">Descrição do Imóvel</h3>
                  <p className="text-slate-600 text-sm leading-relaxed whitespace-pre-wrap">{property.description}</p>
                </div>
              )}
              <div className="border-t border-slate-100 pt-4">
                <h3 className="text-base font-semibold text-slate-900 mb-3">Detalhes do Imóvel</h3>
                <div className="space-y-0">
                  {property.neighborhood && (
                    <div className="flex justify-between py-2.5 border-b border-slate-50">
                      <span className="text-slate-500 text-sm">Bairro</span>
                      <span className="font-medium text-slate-900 text-sm">{property.neighborhood}</span>
                    </div>
                  )}
                  {iptuFormatted && (
                    <div className="flex justify-between py-2.5 border-b border-slate-50">
                      <span className="text-slate-500 text-sm">IPTU</span>
                      <span className="font-medium text-slate-900 text-sm">{iptuFormatted}</span>
                    </div>
                  )}
                  {condoFeeFormatted && (
                    <div className="flex justify-between py-2.5 border-b border-slate-50">
                      <span className="text-slate-500 text-sm">Condomínio</span>
                      <span className="font-medium text-slate-900 text-sm">{condoFeeFormatted}</span>
                    </div>
                  )}
                  {property.city && (
                    <div className="flex justify-between py-2.5 border-b border-slate-50">
                      <span className="text-slate-500 text-sm">Cidade</span>
                      <span className="font-medium text-slate-900 text-sm">{property.city}</span>
                    </div>
                  )}
                  {property.address && (
                    <div className="flex justify-between py-2.5">
                      <span className="text-slate-500 text-sm">Endereço</span>
                      <span className="font-medium text-slate-900 text-sm text-right max-w-[60%]">{property.address}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            {/* 🔥 V24: Imóveis que você pode gostar — Fondo BLANCO puro tipo Airbnb */}
            {allProperties.length > 0 && (
              <div className="bg-white rounded-2xl -mx-4 px-4 py-6 md:-mx-6 md:px-6">
                <HorizontalScroll
                  title="Imóveis que você pode gostar"
                  subtitle="Selecionados para você"
                  primaryColor={primaryColor}
                  autoScroll={true}
                  showArrows={true}
                  showGradient={true}
                >
                  {allProperties
                    .filter(p => p.id !== property?.id)
                    .filter(p => p.images?.length > 0)
                    .slice(0, 8)
                    .map(p => (
                      <FeaturedPropertyCard
                        key={p.id}
                        property={p}
                        primaryColor={primaryColor}
                        onClick={(prop) => window.location.href = getPublicPropertyUrl(prop.id)}
                      />
                    ))}
                </HorizontalScroll>
              </div>
            )}
            <div className="pt-4 border-t border-slate-100">
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-5 md:p-6 rounded-2xl">
                <div className="text-center mb-4">
                  <h3 className="text-lg md:text-xl font-bold text-slate-800 mb-1">Interessado neste imóvel?</h3>
                  <p className="text-slate-600 text-sm">Fale diretamente com o corretor e tire todas as suas dúvidas</p>
                  {company?.telefoneWhatsApp && (
                    <p className="text-xs text-slate-400 mt-1">WhatsApp: {company.telefoneWhatsApp}</p>
                  )}
                </div>
                <div className="flex flex-col sm:flex-row gap-2.5 justify-center">
                  <motion.a
                    whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                    href={getWhatsAppLink(company, propertyUrl, nomeCliente, property)}
                    target="_blank" rel="noreferrer"
                    className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl text-sm font-semibold transition-all shadow-md hover:shadow-lg"
                  >
                    <MessageCircle className="w-5 h-5" /> Falar com Corretor
                  </motion.a>
                  <motion.button
                    whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                    onClick={() => setShowShareModal(true)}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 bg-white border border-slate-200 hover:border-slate-300 text-slate-700 rounded-xl text-sm font-semibold transition-all shadow-sm hover:shadow-md"
                  >
                    <Share2 className="w-4 h-4" /> Compartilhar
                  </motion.button>
                </div>
                <div className="flex items-center justify-center gap-3 mt-3 text-xs text-slate-500">
                  <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-emerald-500" /> Resposta rápida</span>
                  <span className="w-1 h-1 bg-slate-300 rounded-full" />
                  <span className="flex items-center gap-1">📱 Atendimento via WhatsApp</span>
                </div>
              </div>
            </div>
            <RecommendedProperties
              currentProperty={property}
              company={company}
              allProperties={allProperties}
              companyId={companyId}
            />
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}
          className="mt-8 text-center text-xs text-slate-400 pb-20 md:pb-8">
          <p>{company?.nome || 'Imobiliária'} © {new Date().getFullYear()} · Todos os direitos reservados</p>
          {company?.website && (
            <a href={company.website} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline mt-1 inline-block">{company.website}</a>
          )}
        </motion.div>
      </div>
      {isMobile && (
        <motion.div
          initial={{ y: 100 }} animate={{ y: 0 }} transition={{ delay: 0.5 }}
          className="fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-lg border-t border-slate-200 shadow-2xl px-3 py-2.5 safe-area-bottom"
        >
          <div className="flex gap-2">
            <motion.a
              whileTap={{ scale: 0.97 }}
              href={getWhatsAppLink(company, propertyUrl, nomeCliente, property)}
              target="_blank" rel="noreferrer"
              className="flex-1 flex items-center justify-center gap-2 h-11 bg-green-600 text-white rounded-xl font-semibold text-sm shadow-md active:bg-green-700 transition-colors"
            >
              <MessageCircle className="w-5 h-5" /> WhatsApp
            </motion.a>
            <motion.button
              whileTap={{ scale: 0.97 }}
              onClick={() => setShowShareModal(true)}
              className="h-11 px-4 bg-slate-100 text-slate-700 rounded-xl font-semibold text-sm flex items-center gap-2 active:bg-slate-200 transition-colors"
            >
              <Share2 className="w-5 h-5" />
            </motion.button>
          </div>
        </motion.div>
      )}
      {!isMobile && (
        <FloatingShareButton property={property} primaryColor={primaryColor} />
      )}
      <StrategicShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        property={property}
        url={propertyUrl}
        primaryColor={primaryColor}
      />
    </div>
  );
}